<script lang="ts">
  import { filteredLogs, logsFilter } from '../stores/execution-detail';
  import type { LogLevel } from '../types/debugger';

  let searchText = $state('');
  let levelFilter = $state<LogLevel | 'ALL'>('ALL');
  let expandedRows = $state<Set<string>>(new Set());

  const levelColors: Record<LogLevel, string> = {
    INFO: 'bg-info/20 text-info',
    WARN: 'bg-warning/20 text-warning',
    ERROR: 'bg-error/20 text-error',
    DEBUG: 'bg-text-muted/20 text-text-muted',
  };

  $effect(() => {
    logsFilter.set({ level: levelFilter, search: searchText });
  });

  function toggleRow(id: string) {
    const next = new Set(expandedRows);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    expandedRows = next;
  }

  function formatTime(ts: string): string {
    return new Date(ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 });
  }
</script>

<div class="flex flex-col h-full">
  <!-- Filter Bar -->
  <div class="flex items-center gap-3 px-4 py-2 border-b border-border shrink-0">
    <select
      bind:value={levelFilter}
      aria-label="Log level filter"
      class="bg-bg-input border border-border rounded-md px-2 py-1 text-xs text-text-primary outline-none focus:border-accent transition-colors"
    >
      <option value="ALL">ALL</option>
      <option value="INFO">INFO</option>
      <option value="WARN">WARN</option>
      <option value="ERROR">ERROR</option>
      <option value="DEBUG">DEBUG</option>
    </select>
    <input
      type="text"
      bind:value={searchText}
      placeholder="Filter logs..."
      class="flex-1 bg-bg-input border border-border rounded-md px-3 py-1 text-xs text-text-primary placeholder:text-text-muted outline-none focus:border-accent transition-colors"
    />
    <span class="text-[10px] text-text-muted">{($filteredLogs).length} entries</span>
  </div>

  <!-- Log Entries -->
  <div class="flex-1 overflow-y-auto font-mono text-[11px]">
    {#each $filteredLogs as entry, i (entry.timestamp + i)}
      {@const rowKey = entry.timestamp + i}
      <div class="animate-fade-in-up {i % 2 === 0 ? 'bg-bg-panel' : 'bg-bg-dark/30'}"
      >
        <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
        <div
          class="flex items-start gap-2 px-4 py-1.5 {entry.stackTrace ? 'cursor-pointer hover:bg-bg-hover' : ''}"
          onclick={() => { if (entry.stackTrace) toggleRow(rowKey); }}
          role={entry.stackTrace ? 'button' : undefined}
          tabindex={entry.stackTrace ? 0 : undefined}
          onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (entry.stackTrace) toggleRow(rowKey); } }}
        >
          <span class="text-text-muted shrink-0 w-[90px]">{formatTime(entry.timestamp)}</span>

          <span class="shrink-0 px-1.5 py-0 rounded text-[10px] font-medium {levelColors[entry.level]}">
            {entry.level}
          </span>

          <span class="shrink-0 text-text-muted px-1 rounded bg-bg-card text-[10px]">{entry.source}</span>

          <span class="text-text-primary break-all flex-1">{entry.message}</span>

          {#if entry.stackTrace}
            <svg
              class="w-3 h-3 text-text-muted shrink-0 transition-transform {expandedRows.has(rowKey) ? 'rotate-180' : ''}"
              fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          {/if}
        </div>

        {#if entry.stackTrace && expandedRows.has(rowKey)}
          <div class="mx-4 mb-2 px-3 py-2 rounded bg-error/5 border border-error/20 text-error/80 whitespace-pre-wrap text-[10px] leading-relaxed animate-slide-down">
            {entry.stackTrace}
          </div>
        {/if}
      </div>
    {/each}

    {#if ($filteredLogs).length === 0}
      <div class="flex items-center justify-center h-32 text-text-muted text-xs">
        No log entries match filters
      </div>
    {/if}
  </div>
</div>
